import { Subject } from 'rxjs';
import { PointerEventData } from './pointer-event-data';
import { throttle } from '../utility/throttle-decorator';
import { Camera, Intersection, Object3D, Raycaster, WebGLRenderer, Scene, Vector2, Vector3 } from 'three';

/**
 * Event system for THREE JS. Implementation like Unity3D EventSystem.
 * Reference https://docs.unity3d.com/ScriptReference/EventSystems.EventSystem.html
 */
export class PointerEventEmitter {
  public onPointerDown: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onPointerUp: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onPointereMove: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onPointerEnter: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onPointerExit: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onDragStart: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onDrag: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onDragEnd: Subject<PointerEventData> = new Subject<PointerEventData>();
  public onClick: Subject<PointerEventData> = new Subject<PointerEventData>();

  /**
   * Raycaster
   */
  private raycaster: Raycaster;

  // Current pointer position relative to the rendered element
  private pointerPosition: any;

  // Is the pointer currently down?
  private isPointerDown: boolean;

  // Array of raycast results yielded when the pointer was clicked down. Empty if pointer is not down.
  private pointerDownRaycastResult: Intersection[];

  // Object 3ds which were last intersected by raycasting to pointer coordinates
  private intersectedObjects: Object3D[];

  // Is object3d currently being dragged
  private isDragging: boolean;

  // Objects which are raycasted
  public eventReceivingObjects: Object3D[];
  public dragThreshold = 0.005;

  private pointerDownEventOrigin: any;

  constructor(
    // Camera used for ray casting.
    private camera: Camera,
    scene: Scene,
    private renderer: WebGLRenderer,
  ) {
    this.isPointerDown = false;
    this.isDragging = false;
    this.pointerPosition = new Vector2();
    this.raycaster = new Raycaster(new Vector3(0, 0, 0), new Vector3(1, 0, 0), 0, 100000);
    this.raycaster.params.Line = { threshold: 0 };
    this.intersectedObjects = new Array(0);
    this.eventReceivingObjects = scene.children;
    this.pointerDownRaycastResult = new Array(0);

    this.pointerMove = this.pointerMove.bind(this);
    renderer.domElement.addEventListener('pointermove', this.pointerMove, false);
    renderer.domElement.addEventListener('pointerdown', this.pointerDown, false);
    renderer.domElement.addEventListener('pointerup', this.pointerUp, false);
    renderer.domElement.addEventListener('pointerleave', this.pointerLeave, false);
  }

  dispose(): void {
    this.renderer.domElement.removeEventListener('pointerdown', this.pointerDown, false);
    this.renderer.domElement.removeEventListener('pointerup', this.pointerUp, false);
    this.renderer.domElement.removeEventListener('pointermove', this.pointerMove, false);
    this.renderer.domElement.removeEventListener('pointerleave', this.pointerLeave, false);
  }

  private pointerDown = (event: PointerEvent): void => {
    this.pointerDownEventOrigin = event.target;
    this.isPointerDown = true;
    this.pointerPosition = this.calculatePointerPosition(event);
    const raycastResult = this.raycast(event);
    this.intersectedObjects = raycastResult.map(r => r.object);
    const rayCastedObject = this.intersectedObjects.length > 0 ? this.intersectedObjects[0] : undefined;
    this.pointerDownRaycastResult = raycastResult;
    const pointerEventData = new PointerEventData(event);
    pointerEventData.selectedObjectRaycast = raycastResult.length > 0 ? raycastResult[0] : null;
    pointerEventData.selectedObject = rayCastedObject;
    this.onPointerDown.next(pointerEventData);
  };

  private pointerUp = (event: PointerEvent): void => {
    if (!this.pointerDownEventOrigin || this.pointerDownEventOrigin !== event.target) {
      return;
    }
    this.isPointerDown = false;
    const raycastResult = this.raycast(event);
    this.intersectedObjects = raycastResult.map(r => r.object);
    const pointerEventData = new PointerEventData(event);
    pointerEventData.selectedObject = raycastResult.length > 0 ? raycastResult[0].object : undefined;
    pointerEventData.selectedObjectRaycast = raycastResult.length > 0 ? raycastResult[0] : null;
    pointerEventData.pointerPosition = this.calculatePointerPosition(event);
    this.pointerDownEventOrigin = null;

    this.onPointerUp.next(pointerEventData);
    if (this.isDragging) {
      this.isDragging = false;
      this.onDragEnd.next(pointerEventData);
    } else if (
      (this.pointerDownRaycastResult.length === 0 && raycastResult.length === 0) ||
      (this.pointerDownRaycastResult.length > 0 &&
        raycastResult.length > 0 &&
        this.pointerDownRaycastResult[0].object === raycastResult[0].object)
    ) {
      this.onClick.next(pointerEventData);
    }

    this.pointerDownRaycastResult = [];
  };

  @throttle(1000 / 60)
  private pointerMove(event: PointerEvent): void {
    const pointerPosition = this.calculatePointerPosition(event);
    const delta = new Vector2(this.pointerPosition.x - pointerPosition.x, this.pointerPosition.y - pointerPosition.y);
    this.pointerPosition = pointerPosition;
    const lastIntersectedObjects = this.intersectedObjects;
    this.intersectedObjects = this.raycast(event).map(r => r.object);
    const pointerEventData = new PointerEventData(event);
    pointerEventData.pointerPosition = pointerPosition;
    pointerEventData.delta = delta;
    pointerEventData.hoveredObjects = this.intersectedObjects;
    pointerEventData.selectedObjectRaycast = this.pointerDownRaycastResult.length > 0 ? this.pointerDownRaycastResult[0] : null;
    pointerEventData.selectedObject = this.pointerDownRaycastResult.length > 0 ? this.pointerDownRaycastResult[0].object : undefined;
    const enteredObjects = this.intersectedObjects.filter(o => !lastIntersectedObjects.find(o1 => o1 === o));
    const exitedObjects = lastIntersectedObjects.filter(o => !this.intersectedObjects.find(o1 => o1 === o));
    pointerEventData.pointerEnterObjects = enteredObjects;
    pointerEventData.pointerLeaveObjects = exitedObjects;
    this.onPointereMove.next(pointerEventData);
    if (enteredObjects.length > 0) {
      this.onPointerEnter.next(pointerEventData);
    }
    if (exitedObjects.length > 0) {
      this.onPointerExit.next(pointerEventData);
    }

    if (!this.isDragging && this.isPointerDown && pointerEventData.delta.length() > this.dragThreshold) {
      this.isDragging = true;
      pointerEventData.isDragging = true;
      this.onDragStart.next(pointerEventData);
    } else if (this.isDragging && this.isPointerDown) {
      pointerEventData.isDragging = true;
      this.onDrag.next(pointerEventData);
    }
  }

  private pointerLeave = (event: PointerEvent): void => {
    if (this.isPointerDown) {
      this.pointerUp(event);
    }
  };

  /**
   * Raycast from camera into pointer coordinates and return first intersected object, if any.
   */
  private raycast(pointerEvent: PointerEvent): Intersection[] {
    const pointerPosition = this.calculatePointerPosition(pointerEvent);
    this.raycaster.setFromCamera(new Vector2(pointerPosition.x, pointerPosition.y), this.camera);
    const raycastResult = this.raycaster.intersectObjects(this.eventReceivingObjects, true).slice(0, 1);
    return raycastResult;
  }

  private calculatePointerPosition(pointerEvent: PointerEvent): Vector2 {
    const topW = this.renderer.domElement.getBoundingClientRect().left;
    const topH = this.renderer.domElement.getBoundingClientRect().top;
    const x = ((pointerEvent.clientX - topW) / this.renderer.domElement.width) * 2 - 1;
    const y = -((pointerEvent.clientY - topH) / this.renderer.domElement.height) * 2 + 1;
    return new Vector2(x, y);
  }
}
